import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  alpha,
  Divider,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Close,
  Description,
  Image,
  TableChart,
  Slideshow,
  Delete,
  Download,
  Refresh,
  InsertDriveFile,
  PictureAsPdf,
  CheckCircle as CompletedIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Sync as ProcessingIcon,
  CloudUpload,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { apiClient } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface Document {
  id: number;
  document_name: string;
  document_type: string;
  file_size: number;
  status: string;
  processing_status: string;
  uploaded_at: string;
  document_path: string;
}

interface UploadedDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onUploadComplete?: () => void;
}

// 上传文件项
interface FileUploadItem {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

// 支持的文件类型
const ACCEPTED_TYPES: Record<string, { label: string }> = {
  'application/pdf': { label: 'PDF' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { label: 'DOCX' },
  'application/msword': { label: 'DOC' },
  'application/vnd.ms-excel': { label: 'XLS' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { label: 'XLSX' },
  'application/vnd.ms-powerpoint': { label: 'PPT' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { label: 'PPTX' },
  'text/plain': { label: 'TXT' },
  'image/png': { label: 'PNG' },
  'image/jpeg': { label: 'JPG' },
};

// 文件类型图标映射
const getFileIcon = (fileType: string) => {
  const type = fileType.toLowerCase();
  if (type === '.pdf') return <PictureAsPdf color="error" />;
  if (['.doc', '.docx'].includes(type)) return <Description color="primary" />;
  if (['.xls', '.xlsx'].includes(type)) return <TableChart color="success" />;
  if (['.ppt', '.pptx'].includes(type)) return <Slideshow color="warning" />;
  if (['.png', '.jpg', '.jpeg', '.gif'].includes(type)) return <Image color="info" />;
  return <InsertDriveFile />;
};

// 文件类型标签
const getFileTypeLabel = (fileType: string) => {
  const type = fileType.toLowerCase();
  if (type === '.pdf') return 'PDF';
  if (type === '.doc') return 'DOC';
  if (type === '.docx') return 'DOCX';
  if (type === '.xls') return 'XLS';
  if (type === '.xlsx') return 'XLSX';
  if (type === '.ppt') return 'PPT';
  if (type === '.pptx') return 'PPTX';
  if (['.png', '.jpg', '.jpeg', '.gif'].includes(type)) return '图片';
  if (type === '.txt') return 'TXT';
  if (type === '.md') return 'MD';
  return type.replace('.', '').toUpperCase();
};

// 格式化文件大小
const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 格式化日期（处理时区）
const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  try {
    // 如果时间字符串没有时区信息，假设为 UTC 时间
    let dateStr = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('T')) {
      // 纯日期时间字符串，假设为本地时间
      dateStr = dateString;
    } else if (!dateString.endsWith('Z') && dateString.includes('T')) {
      // ISO 格式但没有 Z，添加 Z 表示 UTC
      dateStr = dateString + 'Z';
    }
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Shanghai',
    });
  } catch {
    return dateString;
  }
};

// 处理状态颜色
const getProcessingStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
    case 'pending':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
};

// 处理状态文本
const getProcessingStatusText = (status: string) => {
  switch (status) {
    case 'completed':
      return '已完成';
    case 'processing':
      return '处理中';
    case 'pending':
      return '待处理';
    case 'failed':
      return '处理失败';
    default:
      return status;
  }
};

const UploadedDocumentsDialog: React.FC<UploadedDocumentsDialogProps> = ({
  open,
  onClose,
  projectId,
  onUploadComplete,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  // 上传相关状态
  const [uploadExpanded, setUploadExpanded] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<FileUploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  // 判断用户是否有管理员权限
  const isAdmin = user?.role === 'super_admin';

  // 知识图谱状态
  const [kgStatus, setKgStatus] = useState<{
    status: string;
    progress: number;
    total_documents: number;
    converted_documents: number;
    built_documents: number;
    conversion_progress: number;
    build_progress: number;
    processing_status?: {
      converted: number;
      processing: number;
      failed: number;
      pending: number;
    };
    cognee_status?: {
      completed: number;
      processing: number;
      failed: number;
      pending: number;
    };
  }>({
    status: 'idle',
    progress: 0,
    total_documents: 0,
    converted_documents: 0,
    built_documents: 0,
    conversion_progress: 0,
    build_progress: 0,
  });

  // 获取文档列表
  const fetchDocuments = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.documents.getList(projectId);
      if (response.success && response.data?.documents) {
        setDocuments(response.data.documents);
      } else {
        setError(response.message || '获取文档列表失败');
      }
    } catch (err: any) {
      setError(err.message || '获取文档列表失败');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // 获取知识图谱状态
  const fetchKgStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.knowledgeGraph.getStatus(projectId);
      if (response.success) {
        setKgStatus(response.data);
      }
    } catch (error) {
      // 静默处理错误
    }
  }, [projectId]);

  // 知识图谱状态文本
  const getKgStatusText = (status?: string) => {
    switch (status) {
      case 'not_started': return '未开始';
      case 'pending_update': return '待更新';
      case 'building': return '构建中';
      case 'completed': return '已完成';
      case 'failed': return '构建失败';
      case 'idle': return '未开始';
      case 'processing': return '处理中';
      default: return status || '未知';
    }
  };

  // 构建图谱
  const handleRebuild = async () => {
    if (!projectId) return;
    try {
      await apiClient.knowledgeGraph.rebuild(projectId, false);
      // 立即刷新知识图谱状态
      fetchKgStatus();
      setTimeout(() => {
        fetchKgStatus();
      }, 1000);
    } catch (error: any) {
      setError(error.message || '构建图谱失败');
    }
  };

  // 强制重新构建（管理员）
  const handleForceRebuild = async () => {
    if (!projectId) return;

    const confirmed = window.confirm(
      '确定要强制重新构建所有已转换的文档吗？\n\n' +
      '这将会：\n' +
      '• 重新处理所有已完成转换的文档（包括已经构建过的文档）\n' +
      '• 可能需要较长时间\n' +
      '• 建议仅在需要更新知识图谱时使用'
    );

    if (!confirmed) return;

    try {
      await apiClient.knowledgeGraph.rebuild(projectId, true);
      fetchKgStatus();
      setTimeout(() => {
        fetchKgStatus();
      }, 1000);
    } catch (error: any) {
      setError(error.message || '强制构建图谱失败');
    }
  };

  // 打开时获取数据
  useEffect(() => {
    if (open) {
      fetchDocuments();
      fetchKgStatus();
    }
  }, [open, fetchDocuments, fetchKgStatus]);

  // 轮询知识图谱状态（构建中时每5秒更新）
  useEffect(() => {
    if (!open || kgStatus.status !== 'building') return;

    const interval = setInterval(() => {
      fetchKgStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [open, kgStatus.status, fetchKgStatus]);

  // 下载文档
  const handleDownload = async (doc: Document) => {
    try {
      const blob = await apiClient.documents.download(projectId, doc.document_name);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.document_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || '下载失败');
    }
  };

  // 删除文档
  const handleDelete = async (doc: Document) => {
    if (!window.confirm(`确定要删除文档 "${doc.document_name}" 吗？`)) {
      return;
    }

    setDeleting(doc.id);
    setError(null);

    try {
      const response = await apiClient.documents.delete(projectId, doc.id.toString());
      if (response.success) {
        setDocuments(documents.filter((d) => d.id !== doc.id));
      } else {
        setError(response.message || '删除失败');
      }
    } catch (err: any) {
      setError(err.message || '删除失败');
    } finally {
      setDeleting(null);
    }
  };

  // ==================== 上传相关功能 ====================

  // 拖拽事件处理
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(file =>
      Object.keys(ACCEPTED_TYPES).includes(file.type) ||
      Object.values(ACCEPTED_TYPES).some(({ label }) =>
        file.name.toLowerCase().endsWith(label.toLowerCase())
      )
    );

    if (validFiles.length > 0) {
      addUploadFiles(validFiles);
    } else {
      setError('不支持上传的文件格式');
    }
  }, []);

  const addUploadFiles = (newFiles: File[]) => {
    const fileItems: FileUploadItem[] = newFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending'
    }));
    setUploadFiles(prev => [...prev, ...fileItems]);
    setError(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      addUploadFiles(selectedFiles);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeUploadFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadSingleFile = async (fileItem: FileUploadItem): Promise<void> => {
    const { file } = fileItem;
    try {
      await apiClient.documents.uploadToUploadDir(
        projectId,
        file,
        (progress) => {
          setUploadFiles(prev => prev.map(item =>
            item.file === file
              ? { ...item, progress, status: 'uploading' as const }
              : item
          ));
        }
      );
      setUploadFiles(prev => prev.map(item =>
        item.file === file
          ? { ...item, progress: 100, status: 'success' as const }
          : item
      ));
    } catch (err: any) {
      setUploadFiles(prev => prev.map(item =>
        item.file === file
          ? { ...item, status: 'error' as const, error: err.message || '上传失败' }
          : item
      ));
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    setIsUploading(true);
    setError(null);

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < uploadFiles.length; i++) {
      const fileItem = uploadFiles[i];
      if (fileItem.status === 'pending') {
        await uploadSingleFile(fileItem);
      }
    }

    // 使用状态更新后的回调来检查结果
    setUploadFiles(prev => {
      successCount = prev.filter(item => item.status === 'success').length;
      failCount = prev.filter(item => item.status === 'error').length;
      return prev;
    });

    setIsUploading(false);

    // 延迟检查以确保状态已更新
    setTimeout(() => {
      setUploadFiles(prev => {
        const allSuccessful = prev.length > 0 && prev.every(item => item.status === 'success');
        if (allSuccessful) {
          // 刷新文档列表
          fetchDocuments();
          fetchKgStatus();
          // 回调
          onUploadComplete?.();
          // 清空上传列表
          return [];
        }
        return prev;
      });
    }, 100);
  };

  const getFileUploadIcon = (file: File) => {
    const typeInfo = Object.entries(ACCEPTED_TYPES).find(([type]) =>
      file.type === type || file.name.toLowerCase().endsWith(type.split('/')[1])
    );
    if (!typeInfo) return <InsertDriveFile />;
    const label = typeInfo[1].label;
    if (label === 'PDF') return <PictureAsPdf color="error" />;
    if (['DOC', 'DOCX'].includes(label)) return <Description color="primary" />;
    if (['XLS', 'XLSX'].includes(label)) return <TableChart color="success" />;
    if (['PPT', 'PPTX'].includes(label)) return <Slideshow color="warning" />;
    if (['PNG', 'JPG'].includes(label)) return <Image color="info" />;
    return <InsertDriveFile />;
  };

  const formatUploadFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 400,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsertDriveFile color="primary" />
          <Typography variant="h6">文档管理</Typography>
          <Chip
            size="small"
            label={`${documents.length} 个文档`}
            color="primary"
            variant="outlined"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="刷新">
            <IconButton onClick={fetchDocuments} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 上传文档区域 */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              py: 1,
              '&:hover': { bgcolor: 'action.hover' },
              borderRadius: 1,
              px: 1,
            }}
            onClick={() => setUploadExpanded(!uploadExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudUpload color="primary" />
              <Typography variant="subtitle2" fontWeight="bold">
                上传新文档
              </Typography>
            </Box>
            {uploadExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </Box>

          <Collapse in={uploadExpanded}>
            <Box sx={{ mt: 1.5 }}>
              {/* 拖拽上传区域 */}
              <Box
                sx={{
                  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  bgcolor: isDragging
                    ? alpha(theme.palette.primary.main, 0.05)
                    : alpha(theme.palette.action.hover, 0.02),
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.action.hover, 0.05),
                    borderColor: theme.palette.primary.main
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <CloudUpload sx={{ fontSize: 36, color: isDragging ? 'primary.main' : 'action.disabled', mb: 1 }} />
                <Typography variant="body2">
                  {isDragging ? '松开以上传文件' : '拖拽文件到此处或点击选择'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  支持 PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, PNG, JPG
                </Typography>
              </Box>

              {/* 待上传文件列表 */}
              {uploadFiles.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    待上传文件 ({uploadFiles.length})
                  </Typography>
                  <List dense sx={{ maxHeight: 150, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {uploadFiles.map((fileItem, index) => (
                      <ListItem key={index} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getFileUploadIcon(fileItem.file)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {fileItem.file.name}
                              </Typography>
                              <Chip size="small" label={formatUploadFileSize(fileItem.file.size)} sx={{ height: 18, fontSize: '0.65rem' }} />
                            </Box>
                          }
                          secondary={
                            fileItem.status === 'uploading' ? (
                              <Box sx={{ mt: 0.5 }}>
                                <LinearProgress variant="determinate" value={fileItem.progress} sx={{ height: 4, borderRadius: 1 }} />
                                <Typography variant="caption">{fileItem.progress}%</Typography>
                              </Box>
                            ) : fileItem.status === 'success' ? (
                              <Typography variant="caption" color="success.main">上传成功</Typography>
                            ) : fileItem.status === 'error' ? (
                              <Typography variant="caption" color="error.main">{fileItem.error}</Typography>
                            ) : null
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton size="small" onClick={() => removeUploadFile(index)} disabled={isUploading}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  {/* 操作按钮 */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setUploadFiles([])}
                      disabled={isUploading}
                    >
                      清空
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CloudUpload />}
                      onClick={handleUpload}
                      disabled={isUploading || uploadFiles.length === 0}
                    >
                      {isUploading ? '上传中...' : '开始上传'}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* 文档列表 */}
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
          已上传文档
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              color: 'text.secondary',
            }}
          >
            <InsertDriveFile sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography>暂无已上传的文档</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              点击"上传文档"按钮上传绩效相关文档
            </Typography>
          </Box>
        ) : (
          <List>
            {documents.map((doc) => (
              <ListItem
                key={doc.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  },
                }}
              >
                <ListItemIcon>{getFileIcon(doc.document_type)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 250,
                        }}
                      >
                        {doc.document_name}
                      </Typography>
                      <Chip
                        size="small"
                        label={getFileTypeLabel(doc.document_type)}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Chip
                        size="small"
                        label={getProcessingStatusText(doc.processing_status)}
                        color={getProcessingStatusColor(doc.processing_status)}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        大小: {formatFileSize(doc.file_size)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        上传时间: {formatDate(doc.uploaded_at)}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="下载">
                    <IconButton
                      edge="end"
                      onClick={() => handleDownload(doc)}
                      size="small"
                      sx={{ mr: 0.5 }}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="删除">
                    <IconButton
                      edge="end"
                      onClick={() => handleDelete(doc)}
                      color="error"
                      size="small"
                      disabled={deleting === doc.id}
                    >
                      {deleting === doc.id ? (
                        <CircularProgress size={18} color="error" />
                      ) : (
                        <Delete fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {/* 知识图谱构建状态 */}
        <Box sx={{ mt: 3, borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            知识图谱构建状态
          </Typography>

          <Box sx={{ mb: 2 }}>
            {kgStatus ? (
              <Box>
                {/* 主要状态显示 */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {kgStatus.status === 'building' && <ProcessingIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                    {kgStatus.status === 'pending_update' && <PendingIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                    {kgStatus.status === 'completed' && <CompletedIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                    {kgStatus.status === 'failed' && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                    {kgStatus.status === 'not_started' && <PendingIcon sx={{ fontSize: 16, color: 'default.main' }} />}
                    {(kgStatus.status === 'processing' || kgStatus.status === 'idle') && <PendingIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                    <Chip
                      label={getKgStatusText(kgStatus.status)}
                      size="small"
                      color={
                        kgStatus.status === 'building' ? 'info' :
                          kgStatus.status === 'pending_update' ? 'warning' :
                            kgStatus.status === 'completed' ? 'success' :
                              kgStatus.status === 'failed' ? 'error' :
                                kgStatus.status === 'not_started' ? 'default' :
                                  kgStatus.status === 'processing' ? 'warning' : 'info'
                      }
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.75rem' }}
                      onClick={handleRebuild}
                      disabled={kgStatus.status === 'building'}
                    >
                      构建图谱
                    </Button>
                    {isAdmin && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ minWidth: 'auto', px: 1.5, py: 0.5, fontSize: '0.75rem' }}
                        onClick={handleForceRebuild}
                        disabled={kgStatus.status === 'building'}
                      >
                        重新构建
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* 详细信息 */}
                <Box sx={{ mb: 1, display: 'block' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    文档总数: {kgStatus.total_documents || 0} |
                    转换完成: <span style={{ color: '#4caf50' }}>{kgStatus.converted_documents || 0}</span> |
                    构建完成: <span style={{ color: kgStatus.built_documents === kgStatus.converted_documents && kgStatus.converted_documents > 0 ? '#4caf50' : '#ff9800' }}>
                      {kgStatus.built_documents || 0}
                    </span>
                  </Typography>

                  {kgStatus.status === 'building' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      转换进度: {kgStatus.conversion_progress || 0}% |
                      构建进度: {kgStatus.build_progress || 0}%
                    </Typography>
                  )}

                  {kgStatus.status === 'completed' && (
                    <Typography variant="caption" color="success.main" sx={{ mb: 0.5, display: 'block' }}>
                      知识图谱构建完成！
                    </Typography>
                  )}

                  {kgStatus.cognee_status && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                      详细状态:
                      <span style={{ color: '#4caf50', marginLeft: '4px' }}>完成{kgStatus.cognee_status.completed}</span> |
                      <span style={{ color: '#ff9800', marginLeft: '4px' }}>处理中{kgStatus.cognee_status.processing}</span> |
                      <span style={{ color: '#f44336', marginLeft: '4px' }}>失败{kgStatus.cognee_status.failed}</span> |
                      <span style={{ color: '#9e9e9e', marginLeft: '4px' }}>待处理{kgStatus.cognee_status.pending}</span>
                    </Typography>
                  )}
                </Box>

                {/* 进度条 */}
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      总体进度
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {kgStatus?.build_progress || 0}% (构建)
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', bgcolor: 'action.hover', borderRadius: 1, height: 6 }}>
                    <Box
                      sx={{
                        width: `${kgStatus?.build_progress || 0}%`,
                        bgcolor: kgStatus?.status === 'building' ? 'info.main' :
                          kgStatus?.status === 'pending_update' ? 'warning.main' :
                            kgStatus?.status === 'completed' ? 'success.main' :
                              kgStatus?.status === 'failed' ? 'error.main' :
                                kgStatus?.status === 'not_started' ? 'grey.400' :
                                  kgStatus?.status === 'processing' ? 'warning.main' : 'grey.400',
                        borderRadius: 1,
                        height: 6,
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  加载中...
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadedDocumentsDialog;
